import type { MorseSymbol } from '@/utils/morse';
import { SCORING_ZONES, getHitQualityByDistance } from '@/utils/timing';
import type { NoteOutcome } from '@/objects/MorseNote';

/**
 * Pure hit detection system for finding and evaluating note hits.
 * Separated from PlayerLane to enable testing without Phaser dependencies.
 */

/**
 * Minimal note representation for hit detection.
 * MorseNoteSprite implements this interface.
 */
export interface PendingNote {
  /** Note's left edge X position (pixels) */
  x: number;
  /** Note width in pixels */
  width: number;
  /** Current outcome status */
  outcome: NoteOutcome;
  /** Morse symbol (dot or dash) */
  symbol: MorseSymbol;
  /** Optional note identifier */
  noteId?: string;
}

export type HitQuality = 'perfect' | 'good' | 'ok' | 'bad';

/**
 * Result of a hit detection operation
 */
export interface HitDetectionResult {
  /** The note that was hit */
  note: PendingNote;
  /** Distance from note start edge to hit zone center (negative = past) */
  distance: number;
  /** Quality tier of the hit */
  quality: HitQuality;
}

/**
 * Pure hit detection algorithm.
 *
 * Key design decisions:
 * - Finds the FIRST pending note within scoring range (not necessarily closest)
 * - Uses note's START edge (left edge) for distance calculation
 * - Returns null if no pending notes are within SCORING_ZONES.ok
 *
 * The "first in range" behavior matches the original PlayerLane implementation
 * and is deterministic (no tie-breaking ambiguity).
 */
export class HitDetector {
  constructor(
    private readonly hitZoneX: number,
    private readonly scoringZones: typeof SCORING_ZONES = SCORING_ZONES
  ) {}

  /**
   * Find the first pending note within scoring range.
   * Returns null if no pending notes exist or all are outside range.
   *
   * @param notes - All notes on the track
   * @param maxDistance - Maximum distance to consider (defaults to SCORING_ZONES.ok)
   * @returns The first pending note within range, or null
   */
  findNearestPendingNote(
    notes: readonly PendingNote[],
    maxDistance: number = this.scoringZones.ok
  ): PendingNote | null {
    for (const note of notes) {
      if (note.outcome !== 'pending') continue;

      const distance = this.getNoteStartDistance(note);
      if (Math.abs(distance) <= maxDistance) {
        return note;
      }
    }
    return null;
  }

  /**
   * Determine if a note is within any scoring zone.
   *
   * @param note - Note to check
   * @param zoneSize - Zone size to check against (defaults to ok zone)
   * @returns True if note's start edge is within the zone
   */
  isNoteInScoringZone(note: PendingNote, zoneSize: number = this.scoringZones.ok): boolean {
    const distance = this.getNoteStartDistance(note);
    return Math.abs(distance) <= zoneSize;
  }

  /**
   * Calculate hit quality based on distance from note start to hit zone.
   *
   * @param note - Note to evaluate
   * @returns Hit quality tier
   */
  getHitQuality(note: PendingNote): HitQuality {
    const distance = this.getNoteStartDistance(note);
    return getHitQualityByDistance(distance);
  }

  /**
   * Full hit detection: finds note and calculates quality.
   *
   * @param notes - All notes on the track
   * @param maxDistance - Maximum distance to consider
   * @returns Hit result with note, distance, and quality, or null if no hit
   */
  detectHit(
    notes: readonly PendingNote[],
    maxDistance: number = this.scoringZones.ok
  ): HitDetectionResult | null {
    const note = this.findNearestPendingNote(notes, maxDistance);
    if (!note) return null;

    const distance = this.getNoteStartDistance(note);
    const quality = this.getHitQuality(note);

    return { note, distance, quality };
  }

  /**
   * Calculate distance from note's START edge to hit zone center.
   * Negative = note has passed hit zone, positive = note approaching.
   *
   * This matches MorseNoteSprite.getStartOffset() behavior.
   */
  private getNoteStartDistance(note: PendingNote): number {
    return note.x - this.hitZoneX;
  }

  /**
   * Calculate distance from note's CENTER to hit zone center.
   */
  getNoteCenterDistance(note: PendingNote): number {
    return (note.x + note.width / 2) - this.hitZoneX;
  }

  /**
   * Calculate distance from note's END edge to hit zone center.
   */
  getNoteEndDistance(note: PendingNote): number {
    return (note.x + note.width) - this.hitZoneX;
  }
}
